import { FilterType, ListingPageUrlState } from '../../../../redux/products/types';
import { PageType } from '../../../../hooks/UrlManager/types';
import { BRAND_CATEGORIES_FILTER_LENGTH_FOR_OTHER_ACTIVE, INDEXABLE_FILTER_SELECTION_LIMIT } from './constant';
import { SeoDecisionObject } from './types';
import { AppliedFilter } from 'Client/components/Generic/FilterAndSortBox/types';

const getPathFilterLengths = (appliedFilters: AppliedFilter<FilterType>[]) =>
    appliedFilters.reduce(
        (acc, filter) => {
            acc[filter.type] = filter.values.length;
            return acc;
        },
        { brands: 0, materials: 0, colors: 0, styles: 0, sizes: 0, categories: 0, pattern: 0, form: 0, pillowType: 0 }
    );

type PathFilterSizes = ReturnType<typeof getPathFilterLengths>;

const getIsFollowFromIndexableFilters = (lengths: PathFilterSizes) =>
    lengths.brands === 0 &&
    lengths.materials <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.colors <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.styles <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.sizes <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.pattern <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.form <= INDEXABLE_FILTER_SELECTION_LIMIT &&
    lengths.pillowType <= INDEXABLE_FILTER_SELECTION_LIMIT;

const getIsFollowFromIndexableSum = ([...lengths]: Array<number>) => {
    const indexableSum = lengths.reduce((acc, number) => acc + number, 0);
    return indexableSum <= INDEXABLE_FILTER_SELECTION_LIMIT;
};

const pageUpperLimitBasedOnPageType = (type: PageType) => {
    const NO_LIMIT = Infinity;

    switch (type) {
        case PageType.BRANDS:
        case PageType.LANDING:
            return 1;
        case PageType.NEWPRODUCTS:
        case PageType.CATEGORY:
            return 5;
        default:
            return NO_LIMIT;
    }
};

const getIsFollowBasedOnPageNumber = (urlState: ListingPageUrlState): boolean => {
    const { listingPageType, page = 1 } = urlState;
    return page <= pageUpperLimitBasedOnPageType(listingPageType);
};

export const createSeoDecisionObject = (urlState: ListingPageUrlState): SeoDecisionObject => {
    const isFollow = false;

    const lengths = getPathFilterLengths(urlState.appliedFilters);
    const indexableFiltersSum = lengths.styles + lengths.colors + lengths.materials + lengths.sizes;
    const isFollowBasedOnPageNumber = getIsFollowBasedOnPageNumber(urlState);
    const isFollowFromIndexableFilters = getIsFollowFromIndexableFilters(lengths);
    const isFollowFromIndexableFiltersAndPageNumber = isFollowFromIndexableFilters && isFollowBasedOnPageNumber;

    switch (urlState.listingPageType) {
        case PageType.ALLPRODUCTS:
            return { isFollow, indexableFiltersSum };

        case PageType.BRANDS: {
            const isFollowBasedOnFilters =
                lengths.categories === BRAND_CATEGORIES_FILTER_LENGTH_FOR_OTHER_ACTIVE
                    ? isFollowFromIndexableFilters
                    : getIsFollowFromIndexableSum([indexableFiltersSum, lengths.brands]);

            return {
                isFollow: isFollowBasedOnFilters && isFollowBasedOnPageNumber,
                indexableFiltersSum,
            };
        }

        case PageType.LANDING: {
            if (lengths.categories > 0) {
                return { isFollow, indexableFiltersSum };
            }
            return {
                isFollow: isFollowFromIndexableFiltersAndPageNumber,
                indexableFiltersSum,
            };
        }

        case PageType.NEWPRODUCTS:
        case PageType.CATEGORY:
        default: {
            return {
                isFollow: isFollowFromIndexableFiltersAndPageNumber,
                indexableFiltersSum,
            };
        }
    }
};
